import { useEffect, lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AnimatePresence } from "framer-motion";
import { LayoutWrapper } from "@/components/layout/LayoutWrapper";
import { ScrollToTop } from "@/components/ScrollToTop";
import { PageTransition } from "@/components/layout/PageTransition";

import Index from "./pages/Index";


const Work = lazy(() => import("./pages/Work"));
const CaseStudyDetail = lazy(() => import("./pages/CaseStudyDetail"));
const Services = lazy(() => import("./pages/Services"));
const BrandStrategy = lazy(() => import("./pages/services/BrandStrategy"));
const ContentCreation = lazy(() => import("./pages/services/ContentCreation"));
const CreativeExecution = lazy(() => import("./pages/services/CreativeExecution"));
const SocialMediaMarketing = lazy(() => import("./pages/services/SocialMediaMarketing"));
const PaidMedia = lazy(() => import("./pages/services/PaidMedia"));
const InfluencerMarketing = lazy(() => import("./pages/services/InfluencerMarketing"));
const GraphicDesign = lazy(() => import("./pages/services/GraphicDesign"));
const WebDevelopment = lazy(() => import("./pages/services/WebDevelopment"));
const OngoingSupport = lazy(() => import("./pages/services/OngoingSupport"));
const Insights = lazy(() => import("./pages/Insights"));
const InsightDetail = lazy(() => import("./pages/InsightDetail"));
const Pulse = lazy(() => import("./pages/Pulse"));
const NotFound = lazy(() => import("./pages/NotFound"));
const ThankYou = lazy(() => import("./pages/ThankYou"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const Terms = lazy(() => import("./pages/Terms"));
const FAQ = lazy(() => import("./pages/FAQ"));
const ForStartups = lazy(() => import("./pages/ForStartups"));
const ForSmallBusinesses = lazy(() => import("./pages/ForSmallBusinesses"));
const ForMediumBusinesses = lazy(() => import("./pages/ForMediumBusinesses"));
const ForEnterprises = lazy(() => import("./pages/ForEnterprises"));
const WhoWeWorkWith = lazy(() => import("./pages/WhoWeWorkWith"));
const ForCEOs = lazy(() => import("./pages/ForCEOs"));
const ForCMOs = lazy(() => import("./pages/ForCMOs"));
const ForHeadsOfMarketing = lazy(() => import("./pages/ForHeadsOfMarketing"));
const ForCOOs = lazy(() => import("./pages/ForCOOs"));
const ForCROs = lazy(() => import("./pages/ForCROs"));
const ForHeadsOfGrowth = lazy(() => import("./pages/ForHeadsOfGrowth"));
const WhoWeReportTo = lazy(() => import("./pages/WhoWeReportTo"));
const AIContent = lazy(() => import("./pages/AIContent"));
const Showreel = lazy(() => import("./pages/Showreel"));
const PitchPage = lazy(() => import("./pages/PitchPage"));
const Careers = lazy(() => import("./pages/Careers"));
const Aesthetics = lazy(() => import("./pages/industries/Aesthetics"));
const Hospitality = lazy(() => import("./pages/industries/Hospitality"));
const Luxury = lazy(() => import("./pages/industries/Luxury"));
const B2BIndustry = lazy(() => import("./pages/industries/B2B"));
const TechIndustry = lazy(() => import("./pages/industries/Tech"));
const Canvas = lazy(() => import("./pages/quotes/Canvas"));
const LaYam = lazy(() => import("./pages/quotes/LaYam"));
const AIAdvantage = lazy(() => import("./pages/AIAdvantage"));
const AIReadiness = lazy(() => import("./pages/services/AIReadiness"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const AdminNewsletter = lazy(() => import("./pages/AdminNewsletter"));
const About = lazy(() => import("./pages/About"));
import { CookieConsent } from "./components/CookieConsent";
import { isAnalyticsReady } from "./lib/analytics";

const queryClient = new QueryClient();

// Track page views on route changes (only if consent given)
const RouteTracker = () => {
  const location = useLocation();

  useEffect(() => {
    if (isAnalyticsReady()) {
      window.gtag("config", "G-77W2PD9MKW", {
        page_path: location.pathname + location.search,
      });
    }
  }, [location]);

  return null;
};

function AnimatedRoutes() {
  const location = useLocation();
  return (
    <AnimatePresence mode="wait">
      <PageTransition key={location.pathname}>
        <Routes location={location}>
          <Route path="/" element={<Index />} />
          <Route path="/work" element={<Work />} />
          <Route path="/work/:slug" element={<CaseStudyDetail />} />
          <Route path="/services" element={<Services />} />
          <Route path="/services/brand-strategy" element={<BrandStrategy />} />
          <Route path="/services/content-creation" element={<ContentCreation />} />
          <Route path="/services/creative-execution" element={<CreativeExecution />} />
          <Route path="/services/social-media-marketing" element={<SocialMediaMarketing />} />
          <Route path="/services/paid-media" element={<PaidMedia />} />
          <Route path="/services/influencer-marketing" element={<InfluencerMarketing />} />
          <Route path="/services/graphic-design" element={<GraphicDesign />} />
          <Route path="/services/web-development" element={<WebDevelopment />} />
          <Route path="/services/ongoing-support" element={<OngoingSupport />} />
          <Route path="/insights" element={<Insights />} />
          <Route path="/insights/pulse" element={<Pulse />} />
          <Route path="/insights/:slug" element={<InsightDetail />} />
          <Route path="/thank-you" element={<ThankYou />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/unsubscribe" element={<Unsubscribe />} />
          <Route path="/privacy-policy" element={<Navigate to="/privacy" replace />} />
          <Route path="/uk-marketing-consultancy" element={<Navigate to="/" replace />} />
          <Route path="/paid-media-ppc-management" element={<Navigate to="/services/paid-media" replace />} />
          <Route path="/brand-strategy-positioning" element={<Navigate to="/services/brand-strategy" replace />} />
          <Route path="/content-creation-strategy" element={<Navigate to="/services/content-creation" replace />} />
          <Route path="/social-media-marketing" element={<Navigate to="/services/social-media-marketing" replace />} />
          <Route path="/terms-of-service" element={<Navigate to="/terms" replace />} />
          <Route path="/spain-marketing-agency" element={<Navigate to="/" replace />} />
          <Route path="/global-brand-strategy" element={<Navigate to="/services/brand-strategy" replace />} />
          <Route path="/us-digital-marketing" element={<Navigate to="/" replace />} />
          <Route path="/en/*" element={<Navigate to="/" replace />} />
          <Route path="/blog/*" element={<Navigate to="/insights" replace />} />
          <Route path="/news/*" element={<Navigate to="/insights" replace />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/who-we-work-with" element={<WhoWeWorkWith />} />
          <Route path="/for-startups" element={<ForStartups />} />
          <Route path="/for-small-businesses" element={<ForSmallBusinesses />} />
          <Route path="/for-medium-businesses" element={<ForMediumBusinesses />} />
          <Route path="/for-enterprises" element={<ForEnterprises />} />
          <Route path="/who-we-report-to" element={<WhoWeReportTo />} />
          <Route path="/for-ceos" element={<ForCEOs />} />
          <Route path="/for-cmos" element={<ForCMOs />} />
          <Route path="/for-heads-of-marketing" element={<ForHeadsOfMarketing />} />
          <Route path="/for-coos" element={<ForCOOs />} />
          <Route path="/for-cros" element={<ForCROs />} />
          <Route path="/for-heads-of-growth" element={<ForHeadsOfGrowth />} />
          <Route path="/superintelligence-content" element={<AIContent />} />
          <Route path="/ai-content" element={<Navigate to="/superintelligence-content" replace />} />
          <Route path="/showreel" element={<Showreel />} />
          <Route path="/new/:id" element={<PitchPage />} />
          <Route path="/careers" element={<Careers />} />
          <Route path="/industries/aesthetics" element={<Aesthetics />} />
          <Route path="/industries/hospitality" element={<Hospitality />} />
          <Route path="/industries/luxury" element={<Luxury />} />
          <Route path="/industries/b2b" element={<B2BIndustry />} />
          <Route path="/industries/tech" element={<TechIndustry />} />
          <Route path="/quotes/canvas" element={<Canvas />} />
          <Route path="/quotes/layam" element={<LaYam />} />
          <Route path="/superintelligence-advantage" element={<AIAdvantage />} />
          <Route path="/ai-advantage" element={<Navigate to="/superintelligence-advantage" replace />} />
          <Route path="/services/superintelligence-readiness" element={<AIReadiness />} />
          <Route path="/services/ai-readiness" element={<Navigate to="/services/superintelligence-readiness" replace />} />
          <Route path="/admin/newsletter" element={<AdminNewsletter />} />
          <Route path="/about" element={<About />} />
          <Route path="/team" element={<Navigate to="/about" replace />} />
          <Route path="*" element={<NotFound />} />

        </Routes>
      </PageTransition>
    </AnimatePresence>
  );
}

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <RouteTracker />
          <ScrollToTop />
          <div className="flex flex-col min-h-screen">
            <LayoutWrapper>
              <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin" /></div>}>
                <AnimatedRoutes />
              </Suspense>
            </LayoutWrapper>
            <CookieConsent />
          </div>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
